import { CustomerModel } from 'api-hooks/customer';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { Option } from 'baseui/select';
import { UserStatusEnum } from 'common/constants/enum';
import { themedStyled } from 'common/styles/styletron.theme';
import { toTitleCase, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import useDialog from 'hooks/use-dialog';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import EmployeeSelectInput from 'legacy-modules/master/select-input/employee-select-input';
import CustomerBillingSelectInput from 'legacy-modules/sales/component/customer-billing-select-input';
import CustomerSalesWarningToast from 'legacy-modules/sales/component/customer-sales-warning-toast';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import NoticeSuspendDialog from 'legacy-modules/sales/notice-suspend-dialog';
import {
  CardSection,
  HalfContainer,
  OneThirdContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { HotelSaleFormType } from './form-type';

interface Props {
  hotelSale?: HotelSaleModel;
}

function transformer(item: any): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.address, item.phone]),
  };
}

export default function SalesDetailCard(props: Props) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<HotelSaleFormType>();
  const { editable } = useFormState();
  const [prevCustomerId, setPrevCustomerId] = React.useState<
    string | undefined
  >(undefined);

  const dialog = useDialog();

  const [customerId, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['customerId', 'total', 'remainingTransactionQuota'],
  });

  const showLimitTransactionDialog = React.useCallback(() => {
    dialog.showCustom({
      render: (close) => <NoticeSuspendDialog onClose={close} type="limited" />,
    });
  }, [dialog]);

  const isExceedingQuota =
    editable && remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    if (isExceedingQuota) {
      showLimitTransactionDialog();
    }
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, showLimitTransactionDialog, isExceedingQuota]);

  React.useEffect(() => {
    if (!customerId || customerId === '') {
      setValue('customerId', '');
      setValue('customerBillingOptions', []);
      setValue('customerBillingId', '');
      setValue('address', '');
      setValue('phone', '');
      setValue('hasDueReceivable', false);
      setValue('customerStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [customerId, setValue]);

  const onAfterDetailChangeCustomer = React.useCallback(
    (customer: CustomerModel) => {
      const _options = (customer?.billings || []).map((billing) =>
        transformer(billing),
      );
      setValue('customerBillingOptions', _options);
      setValue('address', toTitleCase(customer.address || ''));
      setValue('phone', customer.phone);

      const remainingTransactionQuota =
        customer.system.remainingTransactionQuota;
      const isExceedingQuota =
        customer.system.isTransactionLimited &&
        total > remainingTransactionQuota;

      setValue('isExceedingQuota', isExceedingQuota);
      setValue('remainingTransactionQuota', remainingTransactionQuota || null);

      if (editable) {
        setValue(
          'customerBillingId',
          _options.length ? (_options?.[0]?.id as string) || '' : '',
        );

        if (prevCustomerId !== customer.id) {
          setPrevCustomerId(customer.id);
          if (prevCustomerId || !props.hotelSale) {
            if (customer.status === UserStatusEnum.UnderSurveillance) {
              dialog.showCustom({
                render: (close) => (
                  <NoticeSuspendDialog onClose={close} type="suspend" />
                ),
              });
            }
            if (customer.hasDueReceivables) {
              dialog.showCustom({
                render: (close) => (
                  <NoticeSuspendDialog onClose={close} type="receivable" />
                ),
              });
            }
            if (isExceedingQuota) showLimitTransactionDialog();
          }

          setValue('hasDueReceivable', customer.hasDueReceivables);
          setValue('customerStatus', customer.status);
        }
      }
    },
    [
      dialog,
      editable,
      prevCustomerId,
      props.hotelSale,
      setValue,
      showLimitTransactionDialog,
      total,
    ],
  );

  return (
    <CardSection>
      <Text variant="HeadingXSmall">{t('ticketSales:sales-details')}</Text>
      <SeparatorVertical $margin={24} />
      <SectionContainer>
        {props.hotelSale && (
          <HalfContainer>
            <Input
              type="text"
              name="number"
              label={t('common:invoice_number')}
              placeholder={t('common:invoice_number')}
              required
            />
          </HalfContainer>
        )}
        <HalfContainer
          $style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <TransactionAt
            name="transactionAt"
            clearable={false}
            hasTime
            required
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <OneThirdContainer>
          <EmployeeSelectInput
            label={t('invoice:operator')}
            placeholder={t('invoice:operator')}
            name="adminId"
            disabled
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <EmployeeSelectInput
            name="operatorId"
            label={t('invoice:sales')}
            placeholder={t('invoice:sales')}
            required
          />
        </OneThirdContainer>
        <OneThirdContainer>
          <EmployeeSelectInput
            name="salesmanId"
            label={t('invoice:lead')}
            placeholder={t('invoice:lead')}
          />
        </OneThirdContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <CustomerSelectInput
            name="customerId"
            onAfterDetailChange={onAfterDetailChangeCustomer}
            required
            isShowTransactionQuota
          />
        </HalfContainer>
        <HalfContainer>
          <CustomerBillingSelectInput name="customerBillingId" />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <Input
            type="text"
            name="phone"
            label={t('common:phone_number')}
            placeholder={t('common:phone_number')}
            disabled
          />
        </HalfContainer>
        <HalfContainer>
          <Input
            type="text"
            name="address"
            label={t('common:address')}
            placeholder={t('common:address')}
            disabled
            noMargin
            fontCase="TitleCase"
          />
        </HalfContainer>
      </SectionContainer>
      <CustomerSalesWarningToast />
    </CardSection>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
